import { STEP_ERROR, STEP_FIVE, STEP_ONE, STEP_THREE, STEP_TWO } from './constants';
import { hipaaSetupSteps } from './data';
import { today } from './helpers';
import { BaaFieldValue, BaaForm, HipaaSetupPayload, HipaaSetupState, VerifyEmailStepData } from './hipaa-types';

const EMPTY_BAA_FORM: BaaForm = {
  businessName: '',
  businessAddress: '',
  businessState: '',
  customerDate: today(),
  firstName: '',
  lastName: '',
  title: '',
  email: ''
};

export function initHipaaSetupModal(state: HipaaSetupState): HipaaSetupState {
  const currentStepNo = !state.submitted ? STEP_ONE : STEP_FIVE;
  const initStep = hipaaSetupSteps[currentStepNo];

  state.currentStepNo = currentStepNo;
  state.stepItem = initStep;
  state.initialized = true;
  state.stepDisabled = initStep.startDisabled;
  state.completedSteps = {};
  state.baaForm = EMPTY_BAA_FORM;

  // remove all stored states for these fields
  // this is important for when we exit out of the
  // modal without completing all of the steps
  delete state.errorMessage;
  delete state.email;
  delete state.emailVerified;

  if (currentStepNo === STEP_FIVE) {
    // check all of the other steps so that the checkmarks are correct when
    // opening the modal at step five
    state.completedSteps = { 1: true, 2: true, 3: true, 4: true };
  }

  return state;
}

export function getHipaaSetupSuccess(state: HipaaSetupState, payload: Partial<HipaaSetupPayload>): HipaaSetupState {
  const submitted = payload.started && !payload.processed;

  state.started = payload.started;
  state.processed = payload.processed;
  state.submitted = submitted;
  state.email = payload.email;
  state.emailVerified = payload.emailVerified;
  state.loading = false;
  state.baaPdfUrl = payload.baaPdfUrl;
  state.currentStepNo = submitted ? STEP_FIVE : STEP_ONE;

  return state;
}

export function captureStepData(step: number, state: HipaaSetupState, data?: ExplicitAny): HipaaSetupState {
  // only step two and three persist data
  switch (step) {
    case STEP_TWO:
      const { email, verified } = data as VerifyEmailStepData;

      state.email = email;
      state.emailVerified = verified;

      return state;

    case STEP_THREE:
      state.baaForm = data;

      return state;

    default:
      return state;
  }
}

export function moveToNextStep(no: number, state: HipaaSetupState): HipaaSetupState {
  if (state.stepItem?.terminatingStep) {
    return state;
  }

  const nextStepNo = no + 1;
  const nextStep = hipaaSetupSteps[nextStepNo];

  state.stepItem = nextStep;
  state.currentStepNo = nextStepNo;
  state.stepDisabled = nextStep.startDisabled;
  state.completedSteps[no] = true;

  if (nextStepNo === STEP_THREE) {
    // make sure the baa form is set to an empty form with
    // just the invisible email field populated
    state.baaForm = { ...EMPTY_BAA_FORM, email: state.email };
  }

  return state;
}

export function fillBaaFormField(baaFieldValue: BaaFieldValue, state: HipaaSetupState): HipaaSetupState {
  if (state.currentStepNo !== STEP_THREE) {
    // ignore
    return state;
  }

  state.baaForm[baaFieldValue.field] = baaFieldValue.value;

  return state;
}

export function baaFormIsValid(isValid: boolean, state: HipaaSetupState): HipaaSetupState {
  if (state.currentStepNo !== STEP_THREE) {
    // ignore
    return state;
  }

  state.stepDisabled = !isValid;
}

export function setErrorStep(state: HipaaSetupState, payload: Partial<HipaaSetupPayload>): HipaaSetupState {
  // when there is an error, the error message property should be set
  // for now, show an error step and reset the data that's been captured in the
  // redux store to prevent possible leaks
  const nextStep = hipaaSetupSteps[STEP_ERROR];

  state.stepItem = nextStep;
  state.currentStepNo = STEP_ERROR;
  state.stepDisabled = nextStep.startDisabled;
  state.baaForm = EMPTY_BAA_FORM;
  state.errorMessage = payload.errorMessage || (payload as ExplicitAny).error?.message;

  delete state.email;
  delete state.emailVerified;
  delete state.baaPdfUrl;

  return state;
}

export function toggleHipaaSetupModal(state: HipaaSetupState, isModalOpen?: boolean) {
  state.isHipaaModalOpen = isModalOpen ?? false;
}
